// src/pages/settings/ExchangeRate.jsx
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { posCache } from '../../utils/posCache';

const GREEN = '#2e9f86';

const Toggle = ({ value, onChange, label, description }) => (
  <label className="flex items-center justify-between gap-3 cursor-pointer">
    <div>
      <div className="text-sm font-medium text-gray-800">{label}</div>
      {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
    </div>
    <div onClick={() => onChange(!value)}
         className="relative shrink-0 w-11 h-6 rounded-full transition-colors cursor-pointer"
         style={{ backgroundColor: value ? GREEN : '#d1d5db' }}>
      <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
           style={{ transform: value ? 'translateX(20px)' : 'translateX(0)' }} />
    </div>
  </label>
);

export default function ExchangeRate() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [value,     setValue]     = useState('');
  const [showLbp,   setShowLbp]   = useState(true);
  const [secCode,   setSecCode]   = useState('LBP');
  const [secSymbol, setSecSymbol] = useState('ل.ل');
  const [saved,     setSaved]     = useState(false);

  const { data: settings, isLoading } = useQuery(
    ['settings'],
    () => api.get('/api/settings').then(r => r.data),
  );

  useEffect(() => {
    if (!settings) return;
    if (settings.exchange_rate != null && value === '') setValue(String(settings.exchange_rate));
    if (settings?.currency?.show_lbp   != null) setShowLbp(!!settings.currency.show_lbp);
    if (settings?.currency?.sec_code)            setSecCode(settings.currency.sec_code);
    if (settings?.currency?.sec_symbol)          setSecSymbol(settings.currency.sec_symbol);
  }, [settings]); // eslint-disable-line

  const save = useMutation(
    () => api.put('/api/settings', {
      exchange_rate: Number(value),
      currency: { show_lbp: showLbp, sec_code: secCode, sec_symbol: secSymbol },
    }),
    {
      onSuccess: (res) => {
        // Push the merged settings straight into the query cache AND the POS
        // localStorage cache — the POS reads settings from posCache as initialData,
        // so without this the old exchange rate survives an app restart offline.
        const updated = res?.data;
        if (updated && updated.exchange_rate != null) {
          qc.setQueryData(['settings'], updated);
          posCache.settings.save(updated);
        }
        qc.invalidateQueries(['settings']);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      },
      onError: (e) => alert(e?.response?.data?.message || e.message),
    },
  );

  return (
    <div className="p-5 max-w-md">
      <h1 className="text-2xl font-bold text-gray-800 mb-5">{t('settings.exchangeRate', 'Exchange Rate')}</h1>
      <div className="bg-white rounded-xl border p-5 space-y-5">

        {/* Exchange rate */}
        <div>
          <label className="text-sm text-gray-500">
            {secCode} per 1 USD
          </label>
          <input type="number" value={value} onChange={e => setValue(e.target.value)}
                 disabled={isLoading}
                 className="w-full border rounded px-3 py-2 mt-1 text-lg" placeholder="89500" />
          {settings?.updatedAt && (
            <p className="text-xs text-gray-400 mt-1">
              {t('settings.lastUpdated', 'Last updated')}: {String(settings.updatedAt).slice(0, 10)}
            </p>
          )}
        </div>

        {/* Secondary currency */}
        <div className="border-t pt-4 space-y-3">
          <div className="text-sm font-semibold text-gray-700">Currency Display</div>
          <Toggle
            value={showLbp}
            onChange={setShowLbp}
            label="Show secondary currency"
            description="When off, only USD prices are shown across the entire system"
          />
          {showLbp && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Currency Code (e.g. LBP, EUR)</label>
                <input value={secCode} onChange={e => setSecCode(e.target.value)} maxLength={8}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="LBP" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Currency Symbol (e.g. ل.ل, €)</label>
                <input value={secSymbol} onChange={e => setSecSymbol(e.target.value)} maxLength={8}
                  className="w-full border rounded px-3 py-2 text-sm" placeholder="ل.ل" />
              </div>
              <div className="col-span-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                Preview: 1 USD = {Number(value || 0).toLocaleString()} {secCode} &nbsp;|&nbsp; Symbol: {secSymbol}
              </div>
            </div>
          )}
          {!showLbp && (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              ⚠ Secondary currency fields will be hidden in Products, POS, and Documents.
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 border-t pt-4">
          <button onClick={() => save.mutate()} disabled={save.isLoading || !value}
                  className="px-5 py-2 rounded text-white font-bold disabled:opacity-40"
                  style={{ backgroundColor: GREEN }}>
            {save.isLoading ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </button>
          {saved && <span className="text-emerald-600 font-semibold">✓ {t('common.saved', 'Saved')}</span>}
        </div>
      </div>
    </div>
  );
}